import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-57163ecc"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 2,
  class: "container"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "Tenant Protection Summary", -1))
const _hoisted_4 = { class: "row data center horizontal-center" }
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Amount Collected", -1))
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Amount of Coverage", -1))
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Facility Share", -1))
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Number of Plans", -1))
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Participation", -1))

import { ref, computed, onMounted } from "vue";
import api from "@virgodev/bazaar/functions/api";
import BaseSpinner from "@/components/BaseSpinner.vue";
import Filter from "@/components/Filter.vue";
import { useStore } from "@/store/index";
import BaseLineChart from "@/components/BaseLineChart.vue";
import { parseYMDLocal, monthLabel } from "@/utils/timeSeries";
import "@vueform/multiselect/themes/default.scss";

interface Facility {
  facility_name: string;
}

interface Client {
  name: string;
  facilities: Facility[];
}

interface TppReport {
  start_date: string;
  total_amount_collected: number | string;
  total_amount_of_coverage: number | string;
  total_number_of_plans: number | string;
  total_facility_share: number | string;
  total_tpp_participation: number | string;
  participation_percentage: number | string;
  total_units: number | string;
}

interface TppHeadlineTotals {
  total_amount_collected: number | string;
  total_amount_of_coverage: number | string;
  total_number_of_plans: number | string;
  total_facility_share: number | string;
  total_tpp_participation: number | string;
  participation_percentage: number | string;
  total_units: number | string;
}


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const formSubmitting = ref(false);
const errors = ref("");
const showCalls = ref(false);
const status = ref("loading");

const client = ref<Client | undefined>();
const facilityOptions = ref(
  Array<{ label: string; value: string | null; disabled: boolean }>()
);

const dateOptions = ref([
  { text: "Current month", value: "current_month" },
  { text: "Last month", value: "last_month" },
  { text: "All year", value: "all_year" },
  { text: "Custom Dates", value: "custom" },
]);

const formFields = computed(() => store.clientPortalFormFields);

// server payload
const facilityData = ref<{
  tpp_report: TppReport[];
  tpp_headline_totals?: TppHeadlineTotals | null;
}>({
  tpp_report: [],
});

// formatting helpers
const asInt = (n: number | string) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );
const asUSD = (n: number | string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

// HEADLINE: prefer the single selected-month object if backend provides it
const totals = computed(() => {
  const h = facilityData.value.tpp_headline_totals;
  if (h) {
    return {
      total_amount_collected: Number(h.total_amount_collected ?? 0),
      total_amount_of_coverage: Number(h.total_amount_of_coverage ?? 0),
      total_number_of_plans: Number(h.total_number_of_plans ?? 0),
      total_facility_share: Number(h.total_facility_share ?? 0),
      total_tpp_participation: Number(h.total_tpp_participation ?? 0),
      participation_percentage: Number(h.participation_percentage ?? 0),
      total_units: Number(h.total_units ?? 0),
    };
  }
  // fallback: sum (e.g., all_year)
  return (facilityData.value.tpp_report || []).reduce(
    (
      acc: {
        total_amount_collected: number;
        total_amount_of_coverage: number;
        total_number_of_plans: number;
        total_facility_share: number;
        total_tpp_participation: number;
        participation_percentage: number;
        total_units: number;
      },
      r: TppReport
    ) => {
      acc.total_amount_collected += Number(r.total_amount_collected ?? 0);
      acc.total_amount_of_coverage += Number(r.total_amount_of_coverage ?? 0);
      acc.total_number_of_plans += Number(r.total_number_of_plans ?? 0);
      acc.total_facility_share += Number(r.total_facility_share ?? 0);
      acc.total_tpp_participation += Number(r.total_tpp_participation ?? 0);
      acc.participation_percentage = Number(r.participation_percentage ?? 0);
      acc.total_units = Number(r.total_units ?? 0);
      return acc;
    },
    {
      total_amount_collected: 0,
      total_amount_of_coverage: 0,
      total_number_of_plans: 0,
      total_facility_share: 0,
      total_tpp_participation: 0,
      participation_percentage: 0,
      total_units: 0,
    }
  );
});

// ONE helper builds a chart series for any metric key
const selectedDate = computed(
  () => (store.clientPortalFormFields || {}).selected_date
);

function buildSeries(key: string) {
  // sort by start_date ascending
  const sorted = (facilityData.value.tpp_report || [])
    .slice()
    .sort((a: TppReport, b: TppReport) => {
      const da = parseYMDLocal(a.start_date)?.getTime() ?? 0;
      const db = parseYMDLocal(b.start_date)?.getTime() ?? 0;
      return da - db;
    });

  // windowing for trend: current/last → last 4 rows; all_year/custom → all rows
  const needsTrend =
    selectedDate.value === "current_month" ||
    selectedDate.value === "last_month";
  const rows = needsTrend ? sorted.slice(-4) : sorted;

  const labels = rows.map((r: TppReport) => {
    const d = parseYMDLocal(r.start_date);
    return d ? monthLabel(d) : "Unknown";
  });

  const data = rows.map((r: TppReport) =>
    Number(r[key as keyof TppReport] ?? 0)
  );
  return { labels, data };
}

// expose all series via one computed to avoid repeating code in template
const series = computed(() => ({
  collected: buildSeries("total_amount_collected"),
  coverage: buildSeries("total_amount_of_coverage"),
  share: buildSeries("total_facility_share"),
  plans: buildSeries("total_number_of_plans"),
  participation: buildSeries("total_tpp_participation"),
}));

function toYMD(d: string | Date | null | undefined) {
  return d instanceof Date ? d.toISOString().slice(0, 10) : d;
}

async function getUserInfo() {
  status.value = "loading";
  errors.value = "";
  const response = await api({ url: `client/` });
  if (response.status == 200) {
    client.value = response.body[0] as Client;
    facilityOptions.value = [
      { label: "All Facilities", value: null, disabled: true },
      ...client.value.facilities.map((f: Facility) => ({
        label: f.facility_name,
        value: f.facility_name,
        disabled: false,
      })),
    ];
    await submitForm();
  } else {
    errors.value =
      "Unable to communicate with the server. Please check your connection and try again.";
  }
  status.value = "loaded";
}

async function submitForm(formData?: typeof formFields.value) {
  formSubmitting.value = true;
  errors.value = "";
  if (formData) store.setClientPortalFormFields(formData);
  const raw = formData ?? store.clientPortalFormFields;

  const payload = {
    ...raw,
    start_date: toYMD(raw.start_date),
    end_date: toYMD(raw.end_date),
  };

  if (!payload.selected_facilities?.length) {
    payload.selected_facilities =
      client.value?.facilities.map(
        (f: { facility_name: string }) => f.facility_name
      ) || [];
  }

  const res = await api({
    url: `client/facility_data/`,
    method: "POST",
    json: payload,
  });

  if (res.status === 200) {
    const body = res.body || {};
    const list = Array.isArray(body.tpp_report)
      ? body.tpp_report
      : body.tpp_report
      ? [body.tpp_report]
      : [];

    // coerce numeric fields
    const normalized: TppReport[] = list.map((r: Record<string, unknown>) => ({
      start_date: String(r.start_date ?? ""),
      total_amount_collected: Number(r.total_amount_collected ?? 0),
      total_amount_of_coverage: Number(r.total_amount_of_coverage ?? 0),
      total_number_of_plans: Number(r.total_number_of_plans ?? 0),
      total_facility_share: Number(r.total_facility_share ?? 0),
      total_tpp_participation: Number(r.total_tpp_participation ?? 0),
      total_units: Number(r?.total_units ?? 0),
      participation_percentage: Number(r?.participation_percentage ?? 0),
    }));

    facilityData.value = {
      tpp_report: normalized,
      tpp_headline_totals:
        (body?.tpp_headline_totals as TppHeadlineTotals | null) || null,
    };
  } else {
    errors.value = "Unable to communicate with the server. Please try again.";
  }

  formSubmitting.value = false;
}

function handleFilterSubmit(newFields: typeof formFields.value) {
  store.setClientPortalFormFields(newFields);
  submitForm(newFields);
}
onMounted(() => submitForm());
getUserInfo();

return (_ctx: any,_cache: any) => {
  const _component_BaseAlert = _resolveComponent("BaseAlert")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (errors.value)
      ? (_openBlock(), _createBlock(_component_BaseAlert, {
          key: 0,
          variant: "danger",
          class: "js-form-error"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(errors.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (status.value === 'loading')
      ? (_openBlock(), _createBlock(BaseSpinner, {
          key: 1,
          preset: "large"
        }))
      : _createCommentVNode("", true),
    (client.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("h1", null, _toDisplayString(client.value.name), 1),
          (!showCalls.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(Filter, {
                  "facility-options": facilityOptions.value,
                  "date-options": dateOptions.value,
                  "initial-form-fields": _unref(formFields),
                  "form-submitting": formSubmitting.value,
                  onSubmit: handleFilterSubmit
                }, null, 8, ["facility-options", "date-options", "initial-form-fields", "form-submitting"]),
                _createElementVNode("div", null, [
                  _hoisted_3,
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", null, [
                      _hoisted_5,
                      _createElementVNode("h3", null, _toDisplayString(asUSD(_unref(totals).total_amount_collected)), 1),
                      _createVNode(BaseLineChart, {
                        labels: _unref(series).collected.labels,
                        datasets: [
                { label: 'Amount Collected', data: _unref(series).collected.data },
              ],
                        "y-tick-format": asUSD
                      }, null, 8, ["labels", "datasets"])
                    ]),
                    _createElementVNode("div", null, [
                      _hoisted_6,
                      _createElementVNode("h3", null, _toDisplayString(asUSD(_unref(totals).total_amount_of_coverage)), 1),
                      _createVNode(BaseLineChart, {
                        labels: _unref(series).coverage.labels,
                        datasets: [
                { label: 'Amount of Coverage', data: _unref(series).coverage.data },
              ],
                        "y-tick-format": asUSD
                      }, null, 8, ["labels", "datasets"])
                    ]),
                    _createElementVNode("div", null, [
                      _hoisted_7,
                      _createElementVNode("h3", null, _toDisplayString(asUSD(_unref(totals).total_facility_share)), 1),
                      _createVNode(BaseLineChart, {
                        labels: _unref(series).share.labels,
                        datasets: [{ label: 'Facility Share', data: _unref(series).share.data }],
                        "y-tick-format": asUSD
                      }, null, 8, ["labels", "datasets"])
                    ]),
                    _createElementVNode("div", null, [
                      _hoisted_8,
                      _createElementVNode("h3", null, _toDisplayString(asInt(_unref(totals).total_number_of_plans)), 1),
                      _createVNode(BaseLineChart, {
                        labels: _unref(series).plans.labels,
                        datasets: [
                { label: 'Number of Plans', data: _unref(series).plans.data },
              ],
                        "y-tick-format": asInt
                      }, null, 8, ["labels", "datasets"])
                    ]),
                    _createElementVNode("div", null, [
                      _hoisted_9,
                      _createElementVNode("h3", null, _toDisplayString(_unref(totals).participation_percentage) + " % out of " + _toDisplayString(asInt(_unref(totals).total_units)) + " Total Units ", 1)
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})